import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import {
    buildDoceboAsyncDataCacheKey,
    fetchWithDoceboAccessToken,
    useDoceboAccessToken
} from '~/composables/useDoceboAccessToken';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import {
    buildAcademyTrainingsListingQuery,
    type AcademyTrainingsListingFilters
} from '~/utils/academy/trainings/buildTrainingsListingQuery';
import { ACADEMY_AGGREGATED_REQUEST_TIMEOUT, ACADEMY_TRAININGS_LISTING_KEY_PREFIX } from '~/utils/academy/constants';

function fetchAcademyTrainingsListing(
    filters: AcademyTrainingsListingFilters | undefined,
    headers: HeadersInit,
    signal?: AbortSignal
): Promise<AcademyTrainingsListingResponse> {
    return $fetch<AcademyTrainingsListingResponse>('/api/academy/trainings/all', {
        query: buildAcademyTrainingsListingQuery(filters),
        headers,
        signal
    });
}

export function useFetchAcademyTrainingsListing() {
    const headers = useRequestHeaders(['cookie']);
    const { hasDoceboAccessToken } = useDoceboAccessToken();

    return (filters?: AcademyTrainingsListingFilters, signal?: AbortSignal) =>
        fetchWithDoceboAccessToken(hasDoceboAccessToken.value, () =>
            fetchAcademyTrainingsListing(filters, headers, signal)
        );
}

/**
 * The async data key for one page of the listing. `page` is part of the serialized query, so every
 * loaded page gets its own entry instead of overwriting the one before it.
 */
function buildListingAsyncDataKey(
    filters: AcademyTrainingsListingFilters | undefined,
    hasAccessToken: boolean
): string {
    return buildDoceboAsyncDataCacheKey(
        ACADEMY_TRAININGS_LISTING_KEY_PREFIX,
        buildAcademyTrainingsListingQuery(filters),
        hasAccessToken
    );
}

/**
 * Registers one further page as its own async data entry, so what "load more" appends lives in
 * `payload.data` and `refreshNuxtData` can re-run it.
 *
 * Runs from an event handler rather than from setup, which Nuxt flags in dev (NUXT_E3003) and
 * suggests replacing with `$fetch`. That is the one thing it cannot be: a plain `$fetch` result is
 * unreachable for a refresh, which is the whole point of registering it.
 */
export function useAcademyTrainingsListingPageFetch() {
    const { hasDoceboAccessToken } = useDoceboAccessToken();
    const fetchListing = useFetchAcademyTrainingsListing();

    return (filters: AcademyTrainingsListingFilters) => {
        const key = buildListingAsyncDataKey(filters, hasDoceboAccessToken.value);
        const asyncData = useAsyncData(key, (_nuxtApp, { signal }) => fetchListing(filters, signal), {
            dedupe: 'defer',
            timeout: ACADEMY_AGGREGATED_REQUEST_TIMEOUT
        });

        return Object.assign(asyncData, { key });
    };
}

export function useAcademyTrainingsListingFetch(
    filters?: MaybeRefOrGetter<AcademyTrainingsListingFilters | undefined>
) {
    const { hasDoceboAccessToken } = useDoceboAccessToken();
    const query = computed(() => buildAcademyTrainingsListingQuery(toValue(filters)));
    const querySignature = computed(() => JSON.stringify(query.value));
    const fetchListing = useFetchAcademyTrainingsListing();

    // Scoped per instance so multiple components on the same page will not abort each other's requests.
    let activeListingRequest: AbortController | undefined;
    const beginListingRequest = (externalSignal?: AbortSignal): AbortSignal => {
        activeListingRequest?.abort();

        const controller = new AbortController();
        activeListingRequest = controller;

        if (externalSignal) {
            if (externalSignal.aborted) {
                controller.abort();
            } else {
                externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
            }
        }

        return controller.signal;
    };

    return useAsyncData(
        () => buildListingAsyncDataKey(toValue(filters), hasDoceboAccessToken.value),
        (_nuxtApp, { signal }) => fetchListing(toValue(filters), beginListingRequest(signal)),
        {
            watch: [querySignature, hasDoceboAccessToken],
            dedupe: 'defer',
            timeout: ACADEMY_AGGREGATED_REQUEST_TIMEOUT
        }
    );
}
